import React from 'react';

import { ILocation } from '@rbi-ctg/frontend';
import { IRestaurant } from '@rbi-ctg/store';
import { OrderLocatorTab } from 'state/launchdarkly/variations';
import { IPlaceData } from 'utils/geolocation';

import { IStore } from '../ducks/stores';

export type StoreCardListRef = {
  scrollToTop: () => void;
  scrollToRestaurant: (restaurantId: string) => void;
};

export type FocusConfig = {
  restaurant?: IStore;
  focusReason?: StoreLocatorAction;
  coordinates?: ILocation;
};

export type MapViewRef = {
  focus: (config: FocusConfig) => void;
  mapIsReady: boolean;
};

export enum FocusedBy {
  MAP = 'map',
  LIST = 'list',
  GLOBAL = 'global',
}

export enum StoreLocatorActionType {
  INITIAL_LOAD = 'initialLoad',
  LOCATION_RESET = 'locationReset',
  MANUAL_SEARCH = 'manualSearch',
  AREA_SEARCH = 'areaSearch',
  USER_SELECTION = 'userSelection',
}

export type StoreLocatorActions = {
  [key in StoreLocatorActionType]: boolean;
};

export enum StoreLocatorAction {
  isReset = 'isReset',
  isSearching = 'isSearching',
  isManualSearch = 'isManualSearch',
  isInitial = 'isInitial',
  isUserSelection = 'isUserSelection',
}

export interface IStoreLocatorViewProps {
  searchingNearby: boolean;
  searchingRecent: boolean;
  searchingFavs: boolean;
  rawIsSearching: boolean | undefined;
  isInSupportForm?: boolean;
  /** Nearby-only list (no Recents/Favorites tabs). */
  nearbyOnly?: boolean;
  /** Radio rows + sticky confirm CTA (minimal / offers picker). */
  isOffersSelection?: boolean;
  headerText?: string;
  navigation?: any;
  storesById: Record<string, IRestaurant>;
  actions: React.MutableRefObject<StoreLocatorActions>;
  currentTabKey: OrderLocatorTab;
  currentTabIndex: number;
  currentTabHasError: boolean;
  focusedRestaurant: IStore | null;
  locatorTabs: OrderLocatorTab[];
  activeStore: IStore | null;
  allStores: IStore[];
  flatListRef: React.RefObject<StoreCardListRef | null>;
  mapRef: React.RefObject<MapViewRef | null>;
  closestStore: IStore | null;
  storesByTab: Record<OrderLocatorTab, IStore[]>;
  onSelectStore?: (storeId: string) => void;
  onRestaurantHighlight: OnRestaurantHighlight;
  onTabChange: (tab: OrderLocatorTab) => void;
  onDismissCallback?: () => void;
  onFavoriteClick: () => void;
  onResetUserCoords: () => void;
  onResetManualSearch: () => void;
  onSearchThisArea: (location: ILocation) => void;
  onAddressSelected: (address: string, placeId?: string, placeData?: IPlaceData) => void;
  /** Offer allow/deny: false → grey marker + non-selectable card. */
  isStoreEligible?: (store: IStore) => boolean;
  /** Banner when a picker store is ineligible (menu Tap-here). */
  ineligibleStoreMessage?: string;
  /** List empty state after an offer restriction removes every nearby restaurant. */
  noEligibleStoresMessage?: string;
}

/**
 * onRestaurantHighlight
 * FocusedBy:
 * - map: The restaurant is focused by the map
 * - list: The restaurant is focused by the list
 * - global: The restaurant is focused by the global search
 */
export interface OnRestaurantHighlight {
  ({
    restaurant,
    focusedBy,
    coords,
  }: {
    restaurant?: IStore | null;
    focusedBy?: FocusedBy;
    coords?: ILocation;
  }): void;
}

export interface SearchAllStoresOptions {
  /** Nearby results were already loaded by the async search. */
  skipNearby?: boolean;
}

export interface UseStoreLocatorInitProps {
  mapRef: React.RefObject<MapViewRef | null>;
  activeStore: IStore | null;
  onRestaurantHighlight: OnRestaurantHighlight;
  actions: React.MutableRefObject<Record<StoreLocatorActionType, boolean>>;
  nearbyStore: IStore | null;
  onSearchAllStores: (location?: ILocation | null, options?: SearchAllStoresOptions) => void;
  onSearchNearbyAsync: (location: ILocation) => Promise<IStore[]>;
  /** When true, always refresh nearby results even if a store is already selected. */
  forceNearbySearch?: boolean;
  /** Used when user location is unavailable (e.g. search near the current store). */
  fallbackCoordinates?: ILocation | null;
}
